"use client";

import { type ReactNode, type RefObject, useEffect, useRef } from "react";

import { api } from "@/lib/trpc/react";

// Copying a phrase isn't copying the listing.
const MIN_COPIED_CHARACTERS = 200;

function selectionInside(container: HTMLElement | null): Selection | null {
  const selection = document.getSelection();
  if (
    !container ||
    !selection ||
    selection.rangeCount === 0 ||
    selection.isCollapsed
  ) {
    return null;
  }
  return selection.getRangeAt(0).intersectsNode(container) ? selection : null;
}

function CopyListener({
  containerRef,
  listingToken,
}: {
  containerRef: RefObject<HTMLElement | null>;
  listingToken: string;
}): null {
  const utils = api.useUtils();
  const utilsRef = useRef(utils);
  useEffect(() => {
    utilsRef.current = utils;
  });

  useEffect(() => {
    let sentence: string | null = null;
    let requested = false;

    const onSelectionChange = () => {
      if (requested || !selectionInside(containerRef.current)) {
        return;
      }
      requested = true;
      utilsRef.current.opportunityListing.copySentence
        .fetch({ token: listingToken }, { staleTime: Infinity })
        .then((result) => {
          sentence = result;
        })
        .catch(() => {
          requested = false;
        });
    };

    const onCopy = (event: ClipboardEvent) => {
      const copied =
        selectionInside(containerRef.current)?.toString().trim() ?? "";
      if (
        !sentence ||
        !event.clipboardData ||
        event.defaultPrevented ||
        copied.length < MIN_COPIED_CHARACTERS
      ) {
        return;
      }
      event.clipboardData.setData("text/plain", `${copied}\n\n${sentence}`);
      event.preventDefault();
    };

    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("copy", onCopy);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("copy", onCopy);
    };
  }, [containerRef, listingToken]);

  return null;
}

/**
 * A listing description whose copies carry the copied-listing check's
 * sentence (`services/copy-check` in @repo/api). The sentence exists only in
 * the clipboard, never in the page or its HTML. It is fetched on the first
 * selection inside the description, so a copy made before it arrives stays
 * plain. Without a token, as on previews, this is a plain wrapper.
 */
export function ListingCopyCheck({
  listingToken,
  className,
  children,
}: {
  listingToken: string | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={className}>
      {children}
      {listingToken && (
        <CopyListener containerRef={ref} listingToken={listingToken} />
      )}
    </div>
  );
}
